/** CartonLab — admin Box/Carton Counting Lab.
 *
 *  Exercises the production TI/HI counting pipeline on uploaded pallet photos:
 *  YOLO carton/layer detection → certified front-row / Y-shift counter.
 *
 *  ① Status banner — YOLO operating mode (custom weights / zero-shot /
 *    simulation) so empty results are explained, not mysterious.
 *  ② Parameters — Ti (cartons per layer), Hi (blank = auto from YOLO layer
 *    count), Y-shift threshold floor (px).
 *  ③ Run on test data — chunked POSTs to /api/carton-lab/run (YOLO is
 *    CPU-heavy; the server runs files sequentially).
 *  ④ Results — per-file annotated preview (front row green / back blue /
 *    layers grey + the front-row band), count breakdown, audit reasons.
 *  ⑤ Recount — re-runs ONLY the pure-python counter on the stored boxes with
 *    new Ti/Hi/threshold (no image upload, no YOLO pass — the cheap loop).
 *  ⑥ Comparison table — accumulates runs in session state (nothing persisted).
 */

import { useEffect, useRef, useState } from 'react';
import { apiClient, ApiError } from '@/api/client';

// ── Types (mirror backend schemas) ────────────────────────────────────────────

interface LabStatus {
  mode: 'custom' | 'zero-shot' | 'simulation';
  zero_shot_mode: boolean;
  weights_path: string;
  weights_exists: boolean;
  ultralytics_installed: boolean;
  model_loaded: boolean;
  conf_threshold: number;
  default_y_threshold_px: number;
  max_files_per_run: number;
  // Air-gapped model switcher: every *.pt in the local models dir.
  models: { filename: string; size_mb: number; is_default: boolean }[];
  techniques: Record<string, boolean>;
  active_learning: { total: number; today: number; recent: { file: string; day: string; reasons: string[] }[] };
}

interface TechniqueOut { name: string; enabled: boolean; applied: boolean; summary: string }

interface CartonBoxOut {
  x1: number; y1: number; x2: number; y2: number;
  confidence: number;
  row: 'front' | 'back' | 'excluded';
}

interface CountOut {
  total_cartons: number;
  front_row_count: number;
  back_row_count: number;
  full_layers: number;
  top_layer_cartons: number;
  calculation_confidence: number;
  effective_threshold_px: number;
  requires_human_audit: boolean;
  audit_reason?: string | null;
}

interface ReconciliationOut {
  state: 'MATCH' | 'MISMATCH_SKU' | 'DISCREPANCY_COUNT' | 'TIHI_VIOLATION' | 'UNMAPPED_LOCATION';
  location_code?: string | null;
  expected_cartons?: number | null;
  expected_qty_raw?: number | null;
  expected_sku?: string | null;
  matched_sku?: string | null;
  detail: string;
}

interface CartonFileOut {
  filename: string;
  model_used?: string;
  scene_class?: string;
  scene_source?: 'heuristic' | 'legacy_model';
  scene_confidence?: number | null;
  scene_strategy?: string | null;
  scene_probabilities?: Record<string, number>;
  shadow_scene_class?: string | null;
  shadow_confidence?: number | null;
  shadow_blocked?: boolean;
  scene_agreement?: boolean | null;
  ti_used?: number | null;
  ti_source?: 'manual' | 'tihi' | null;
  reconciliation?: ReconciliationOut | null;
  techniques?: TechniqueOut[];
  detected_layers?: number | null;
  carton_count?: number | null;
  pallet_count?: number;
  yolo_confidence: number;
  inference_ms: number;
  total_ms: number;
  simulation_mode: boolean;
  hi_used?: number | null;
  hi_source?: 'manual' | 'detected' | null;
  boxes: CartonBoxOut[];
  count?: CountOut | null;
  count_error?: string | null;
  preview_b64?: string | null;
  error?: string | null;
}

interface RunResponse {
  ti: number;
  y_threshold: number;
  files: CartonFileOut[];
  summary: Record<string, unknown>;
}

interface RecountFileOut {
  filename: string;
  hi_used?: number | null;
  ti_used?: number | null;
  ti_source?: string | null;
  reconciliation?: ReconciliationOut | null;
  boxes: CartonBoxOut[];
  count?: CountOut | null;
  count_error?: string | null;
  techniques?: TechniqueOut[];
}

interface RecountResponse { ti: number; y_threshold: number; files: RecountFileOut[] }

interface CartonRun {
  id: number;
  label: string;
  ti: number;
  hi: number | null;          // manual Hi at run time (null = auto)
  yThreshold: number;
  model: string;              // weights filename used for detection
  techniques: Record<string, boolean>;
  files: CartonFileOut[];
  recounted: boolean;
}

const TECHNIQUE_LABELS: Record<string, string> = {
  pallet_filter:     'Pallet filter (exclude neighbour boxes)',
  tilt_correction:   'Tilt correction (de-shear rows)',
  column_clustering: 'Column clustering cross-check',
  median_area_check: 'Median-area cross-check (merged masks)',
};

const SCENE_BADGE: Record<string, string> = {
  FullPallet:    'bg-emerald-100 text-emerald-800',
  PartialPallet: 'bg-sky-100 text-sky-800',
  SingleLayer:   'bg-cyan-100 text-cyan-800',
  BulkBroken:    'bg-orange-100 text-orange-800',
  EmptyLocation: 'bg-gray-100 text-gray-700',
  BadImage:      'bg-red-100 text-red-700',
  Unknown:       'bg-gray-100 text-gray-500',
};

const RECON_BADGE: Record<ReconciliationOut['state'], { label: string; cls: string }> = {
  MATCH:             { label: '✓ MATCH',          cls: 'bg-emerald-100 text-emerald-800' },
  DISCREPANCY_COUNT: { label: '≠ COUNT',          cls: 'bg-amber-100 text-amber-800' },
  MISMATCH_SKU:      { label: '⚠ SKU',            cls: 'bg-rose-100 text-rose-800' },
  TIHI_VIOLATION:    { label: '⚠ TIHI',           cls: 'bg-red-100 text-red-700' },
  UNMAPPED_LOCATION: { label: '? LOCATION',       cls: 'bg-gray-200 text-gray-700' },
};

// ── Master context types (SOH + TiHi uploads) ─────────────────────────────────

interface WarehouseItem { id: string; name: string; code: string }

interface MastersSummary {
  tihi: { skus: number; updated_at: string | null };
  soh: { label: string; locations: number; rows: number; updated_at: string } | null;
}

interface FieldMatchOut { column: string | null; confidence: number; method: string }

interface ParsePreview {
  status: 'ok' | 'needs_mapping' | 'error';
  kind: 'tihi' | 'soh';
  sheet: string | null;
  header_row: number;
  mapping: Record<string, FieldMatchOut>;
  columns: { name: string; samples: string[] }[];
  row_count: number;
  rows_considered: number;
  skipped: number;
  warnings: string[];
  error?: string | null;
  sample_rows: Record<string, unknown>[];
}

const MASTER_FIELD_LABELS: Record<string, Record<string, string>> = {
  tihi: {
    sku_code: 'SKU code *', description: 'Description',
    cartons_per_layer: 'Cartons per layer (Ti) *', max_layers: 'Max layers (Hi)',
    units_per_carton: 'Units per carton',
  },
  soh: {
    location_code: 'Location *', sku_code: 'SKU code *', on_hand_qty: 'On-hand qty *',
    description: 'Description', units_per_carton: 'Units per carton',
  },
};

const CHUNK_SIZE = 1;          // one image per request — CPU inference is slow and
                               // the machine is RAM-tight; keeps each request fast
const MAX_TOTAL_FILES = 12;

const MODE_BADGE: Record<LabStatus['mode'], { label: string; cls: string }> = {
  custom:       { label: 'custom weights',  cls: 'bg-emerald-100 text-emerald-800' },
  'zero-shot':  { label: 'zero-shot (COCO)', cls: 'bg-sky-100 text-sky-800' },
  simulation:   { label: 'simulation — no model', cls: 'bg-amber-100 text-amber-800' },
};

// ── Zoom modal ────────────────────────────────────────────────────────────────

function PreviewModal({ file, onClose }: { file: CartonFileOut; onClose: () => void }) {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6" onClick={onClose}>
      {file.preview_b64 ? (
        <img
          src={file.preview_b64}
          alt={file.filename}
          className="max-w-full max-h-[90vh] object-contain rounded shadow-2xl"
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <p className="text-white/60 text-sm">No preview available</p>
      )}
      <p className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white/60 text-xs font-mono">
        {file.filename} · Esc to close
      </p>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function CartonLab() {
  const [status, setStatus] = useState<LabStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Parameters (kept as strings for friendly number inputs)
  const [tiStr, setTiStr] = useState('');
  const [hiStr, setHiStr] = useState('');           // blank = auto (detected layers)
  const [thrStr, setThrStr] = useState('20');
  const [selectedModel, setSelectedModel] = useState('');   // '' = production default
  const [modelWarm, setModelWarm] = useState<'cold' | 'warming' | 'ready'>('cold');
  const [techniques, setTechniques] = useState<Record<string, boolean>>({
    pallet_filter: true, tilt_correction: false,
    column_clustering: false, median_area_check: false,
  });

  const [running, setRunning] = useState(false);
  const [recounting, setRecounting] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [runError, setRunError] = useState<string | null>(null);

  const [runs, setRuns] = useState<CartonRun[]>([]);
  const [activeRunId, setActiveRunId] = useState<number | null>(null);
  const [zoomFile, setZoomFile] = useState<CartonFileOut | null>(null);
  const runCounter = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Master context (SOH ground truth + TiHi registry, per warehouse)
  const [warehouses, setWarehouses] = useState<WarehouseItem[]>([]);
  const [warehouseId, setWarehouseId] = useState('');
  const [masters, setMasters] = useState<MastersSummary | null>(null);
  const [masterPreview, setMasterPreview] = useState<ParsePreview | null>(null);
  const [masterFile, setMasterFile] = useState<File | null>(null);
  const [masterMapping, setMasterMapping] = useState<Record<string, string>>({});
  const [masterBusy, setMasterBusy] = useState(false);
  const [masterMsg, setMasterMsg] = useState<string | null>(null);
  const tihiInputRef = useRef<HTMLInputElement>(null);
  const sohInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    apiClient.get<LabStatus>('/api/carton-lab/status')
      .then(s => {
        setStatus(s);
        setThrStr(String(s.default_y_threshold_px));
        if (s.techniques) setTechniques(t => ({ ...t, ...s.techniques }));
        const def = s.models?.find(m => m.is_default);
        if (def) setSelectedModel(def.filename);
      })
      .catch(e => setLoadError(e instanceof ApiError ? e.message : 'Failed to load Carton Lab.'));
    apiClient.get<WarehouseItem[]>('/api/manage/warehouses')
      .then(setWarehouses)
      .catch(() => setWarehouses([]));
  }, []);

  const refreshMasters = (wid: string) => {
    if (!wid) { setMasters(null); return; }
    apiClient.get<MastersSummary>(`/api/carton-lab/masters?warehouse_id=${wid}`)
      .then(setMasters)
      .catch(() => setMasters(null));
  };

  useEffect(() => {
    setMasterPreview(null); setMasterFile(null); setMasterMsg(null);
    refreshMasters(warehouseId);
  }, [warehouseId]);

  // Pre-warm the chosen model in the background so the first run isn't a
  // ~25s cold-start cliff (model load + torch warmup on the CPU box).  The
  // warmup loads the exact instance the run will reuse.  Fire-and-forget;
  // a failure just means the run pays the cold cost itself.
  useEffect(() => {
    if (!status || status.mode === 'simulation') return;
    // Wait until a model is actually selected — firing with no model warms the
    // wrong (default) instance and the empty body used to 400 the endpoint.
    if (!selectedModel) return;
    let cancelled = false;
    setModelWarm('warming');
    const qs = `?model=${encodeURIComponent(selectedModel)}`;
    apiClient.post<{ ready: boolean }>(`/api/carton-lab/warmup${qs}`, {})
      .then(r => { if (!cancelled) setModelWarm(r.ready ? 'ready' : 'cold'); })
      .catch(() => { if (!cancelled) setModelWarm('cold'); });
    return () => { cancelled = true; };
  }, [selectedModel, status?.mode]);

  const activeRun = runs.find(r => r.id === activeRunId) ?? null;

  const ti = parseInt(tiStr, 10);
  const hi = hiStr.trim() === '' ? null : parseInt(hiStr, 10);
  const thr = parseFloat(thrStr);
  // Ti may be left blank when a TiHi registry is loaded — auto-fills per file.
  const tihiAvailable = !!(warehouseId && masters && masters.tihi.skus > 0);
  const tiOk = (Number.isInteger(ti) && ti >= 1) || (tiStr.trim() === '' && tihiAvailable);
  const paramsValid =
    tiOk &&
    (hi === null || (Number.isInteger(hi) && hi >= 1)) &&
    Number.isFinite(thr) && thr > 0;

  // Recount is offered when the active run's COUNT-side params differ from
  // the inputs (a model change needs a full re-run — detection is the
  // expensive stage; counting params are the cheap loop).
  const paramsDiffer = activeRun != null && paramsValid && (
    activeRun.ti !== ti || activeRun.hi !== hi || activeRun.yThreshold !== thr ||
    JSON.stringify(activeRun.techniques) !== JSON.stringify(techniques)
  );

  // ── ③ Run on uploaded photos ────────────────────────────────────────────────
  const runOnFiles = async (fileList: FileList) => {
    if (!paramsValid) { setRunError('Enter a valid Ti (≥1) before running.'); return; }
    const files = Array.from(fileList).slice(0, MAX_TOTAL_FILES);
    if (files.length === 0) return;

    setRunning(true);
    setRunError(null);
    setProgress({ done: 0, total: files.length });

    const allResults: CartonFileOut[] = [];
    try {
      for (let i = 0; i < files.length; i += CHUNK_SIZE) {
        const chunk = files.slice(i, i + CHUNK_SIZE);
        const form = new FormData();
        chunk.forEach(f => form.append('files', f));
        if (Number.isInteger(ti) && ti >= 1) form.append('ti', String(ti));
        if (hi !== null) form.append('hi', String(hi));
        form.append('y_threshold', String(thr));
        if (selectedModel) form.append('model', selectedModel);
        form.append('techniques', JSON.stringify(techniques));
        if (warehouseId) form.append('warehouse_id', warehouseId);
        const res = await apiClient.postForm<RunResponse>('/api/carton-lab/run', form);
        allResults.push(...res.files);
        setProgress({ done: Math.min(i + CHUNK_SIZE, files.length), total: files.length });
      }

      const run: CartonRun = {
        id: ++runCounter.current,
        label: `Run #${runCounter.current}`,
        ti, hi, yThreshold: thr,
        model: selectedModel || 'best.pt',
        techniques: { ...techniques },
        files: allResults,
        recounted: false,
      };
      setRuns(rs => [...rs, run]);
      setActiveRunId(run.id);
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Run failed.';
      setRunError(/502|504/.test(msg)
        ? 'Inference crashed or timed out (HTTP 502) — usually the model exhausting '
          + 'server memory. Retry with fewer photos; if it persists, check the YOLO '
          + 'model size in the status banner.'
        : msg);
    } finally {
      setRunning(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // ── ⑤ Recount with new params (no YOLO pass) ───────────────────────────────
  const recountActive = async () => {
    if (!activeRun || !paramsValid) return;
    setRecounting(true);
    setRunError(null);
    try {
      const res = await apiClient.post<RecountResponse>('/api/carton-lab/recount', {
        ti: Number.isInteger(ti) && ti >= 1 ? ti : null,
        y_threshold: thr,
        hi_override: hi,
        techniques,
        warehouse_id: warehouseId || null,
        files: activeRun.files
          .filter(f => !f.error)
          .map(f => ({
            filename: f.filename,
            // Auto mode falls back to each file's own detected layer count.
            hi: f.detected_layers ?? f.hi_used ?? null,
            // Excluded (neighbour-pallet) boxes never re-enter the count.
            boxes: f.boxes.filter(b => b.row !== 'excluded').map(b => ({
              x1: b.x1, y1: b.y1, x2: b.x2, y2: b.y2, confidence: b.confidence,
            })),
          })),
      });
      const byName = new Map(res.files.map(f => [f.filename, f]));
      setRuns(rs => rs.map(r => r.id !== activeRun.id ? r : {
        ...r,
        ti, hi, yThreshold: thr,
        techniques: { ...techniques },
        recounted: true,
        files: r.files.map(f => {
          const u = byName.get(f.filename);
          if (!u) return f;
          const excludedBoxes = f.boxes.filter(b => b.row === 'excluded');
          return {
            ...f,
            boxes: [...u.boxes, ...excludedBoxes],
            count: u.count,
            count_error: u.count_error,
            hi_used: u.hi_used,
            hi_source: hi !== null ? 'manual' : f.hi_source,
            ti_used: u.ti_used ?? f.ti_used,
            ti_source: (u.ti_source as CartonFileOut['ti_source']) ?? f.ti_source,
            reconciliation: u.reconciliation ?? f.reconciliation,
            techniques: u.techniques?.length
              ? [...(f.techniques?.filter(t => t.name === 'pallet_filter') ?? []), ...u.techniques]
              : f.techniques,
          };
        }),
      }));
    } catch (e) {
      setRunError(e instanceof ApiError ? e.message : 'Recount failed.');
    } finally {
      setRecounting(false);
    }
  };

  // ── Master context uploads (SOH ground truth / TiHi registry) ───────────────
  const pickMaster = async (kind: 'tihi' | 'soh', file: File) => {
    if (!warehouseId) { setMasterMsg('Select a warehouse first.'); return; }
    setMasterBusy(true); setMasterMsg(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await apiClient.postForm<ParsePreview>(
        `/api/carton-lab/masters/${kind}/preview`, form);
      setMasterFile(file);
      setMasterPreview(res);
      const prefill: Record<string, string> = {};
      Object.entries(res.mapping).forEach(([f, m]) => { if (m.column) prefill[f] = m.column; });
      setMasterMapping(prefill);
      if (res.status === 'error') setMasterMsg(res.error ?? 'Could not read the file.');
    } catch (e) {
      setMasterMsg(e instanceof ApiError ? e.message : 'Preview failed.');
    } finally {
      setMasterBusy(false);
      if (tihiInputRef.current) tihiInputRef.current.value = '';
      if (sohInputRef.current) sohInputRef.current.value = '';
    }
  };

  const commitMaster = async () => {
    if (!masterPreview || !masterFile || !warehouseId) return;
    setMasterBusy(true); setMasterMsg(null);
    try {
      const form = new FormData();
      form.append('file', masterFile);
      form.append('warehouse_id', warehouseId);
      form.append('mapping', JSON.stringify(masterMapping));
      const res = await apiClient.postForm<{ stored: number; locations?: number }>(
        `/api/carton-lab/masters/${masterPreview.kind}/commit`, form);
      setMasterMsg(
        `${masterPreview.kind === 'tihi' ? 'TiHi' : 'SOH'} committed — ${res.stored} row(s)` +
        (res.locations ? `, ${res.locations} location(s).` : '.'));
      setMasterPreview(null); setMasterFile(null);
      refreshMasters(warehouseId);
    } catch (e) {
      setMasterMsg(e instanceof ApiError ? `Commit failed: ${e.message}` : 'Commit failed.');
    } finally {
      setMasterBusy(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  if (loadError) {
    return <div className="p-4 text-sm text-red-600">{loadError}</div>;
  }

  return (
    <div className="space-y-4">
      {/* ── ① Status banner ─────────────────────────────────────────────────── */}
      <section className="rounded-xl border bg-white p-4 space-y-2">
        <div className="flex items-center gap-2 flex-wrap">
          <h3 className="font-semibold text-sm">Box / Carton Counting Lab</h3>
          {status && (
            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${MODE_BADGE[status.mode].cls}`}>
              YOLO: {MODE_BADGE[status.mode].label}
            </span>
          )}
          {status?.model_loaded && (
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
              model loaded
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">
          Runs the production TI/HI pipeline on test photos: YOLO carton/layer/pallet detection,
          then the certified counter — total = (Hi−1)×Ti + front-row. Front-row cartons are outlined
          <span className="font-semibold text-green-700"> green</span>, back rows
          <span className="font-semibold text-blue-700"> blue</span>, the pallet
          <span className="font-semibold text-purple-700"> purple</span>, excluded neighbour-pallet
          boxes <span className="font-semibold text-gray-600">grey</span>, layer boxes light grey.
          Models are loaded from the local <code className="font-mono">/data/models</code> directory and
          inference runs entirely on this machine — weights never leave the host.
        </p>
        {status && status.active_learning?.total > 0 && (
          <p className="text-xs text-muted-foreground">
            🗂 Active learning: <span className="font-semibold">{status.active_learning.today}</span> hard
            example(s) staged today, {status.active_learning.total} total in{' '}
            <code className="font-mono">/data/active_learning/staging</code> awaiting annotation.
          </p>
        )}
        {status?.mode === 'simulation' && (
          <p className="text-xs rounded bg-amber-50 border border-amber-200 text-amber-800 p-2">
            No YOLO model is available on the server ({status.ultralytics_installed
              ? <>weights missing at <code className="font-mono">{status.weights_path}</code></>
              : 'ultralytics package not installed'}).
            Detection will return zero boxes and every photo will be flagged for manual count.
            Upload <code className="font-mono">best.pt</code> via <code className="font-mono">fly sftp</code> or
            enable <code className="font-mono">YOLO_ZERO_SHOT_MODE</code> to test end-to-end.
          </p>
        )}
      </section>

      {/* ── ①b Master context — SOH ground truth + TiHi registry ───────────── */}
      <section className="rounded-xl border bg-white p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <h3 className="font-semibold text-sm">Master context</h3>
          <select
            value={warehouseId}
            onChange={e => setWarehouseId(e.target.value)}
            className="rounded border px-2 py-1.5 text-sm"
          >
            <option value="">— select warehouse —</option>
            {warehouses.map(w => (
              <option key={w.id} value={w.id}>{w.name} ({w.code})</option>
            ))}
          </select>
          {warehouseId && (
            <>
              <button
                onClick={() => tihiInputRef.current?.click()}
                disabled={masterBusy}
                className="rounded-lg border px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                📐 Upload TiHi sheet
              </button>
              <button
                onClick={() => sohInputRef.current?.click()}
                disabled={masterBusy}
                className="rounded-lg border px-3 py-1.5 text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                📊 Upload SOH sheet
              </button>
            </>
          )}
          {masterMsg && <span className="text-xs text-muted-foreground">{masterMsg}</span>}
        </div>
        <input
          ref={tihiInputRef} type="file" accept=".xlsx,.xlsm,.xls,.csv" hidden
          onChange={e => e.target.files?.[0] && pickMaster('tihi', e.target.files[0])}
        />
        <input
          ref={sohInputRef} type="file" accept=".xlsx,.xlsm,.xls,.csv" hidden
          onChange={e => e.target.files?.[0] && pickMaster('soh', e.target.files[0])}
        />

        {!warehouseId ? (
          <p className="text-xs text-muted-foreground">
            Select a warehouse, then upload its SOH and TiHi sheets — columns are auto-mapped
            (any layout), with manual dropdowns when auto-mapping isn't sure. Photos are matched
            to SOH locations by FILENAME, e.g. <code className="font-mono">CA-031-A.jpg</code> or{' '}
            <code className="font-mono">CA-031-A__1500425.jpg</code> (with SKU). Runs then get a
            5-state verdict: MATCH · DISCREPANCY_COUNT · MISMATCH_SKU · TIHI_VIOLATION · UNMAPPED_LOCATION.
          </p>
        ) : masters && (
          <p className="text-xs text-muted-foreground">
            TiHi registry: <b>{masters.tihi.skus}</b> SKU(s)
            {masters.tihi.updated_at && <> · updated {new Date(masters.tihi.updated_at).toLocaleString()}</>}
            {' · '}SOH profile: {masters.soh
              ? <><b>{masters.soh.locations}</b> location(s), {masters.soh.rows} rows — <code className="font-mono">{masters.soh.label}</code></>
              : 'none uploaded'}
          </p>
        )}

        {masterPreview && masterPreview.status !== 'error' && (
          <div className="rounded-lg border bg-gray-50 p-3 space-y-2">
            <p className="text-xs font-medium">
              {masterPreview.kind === 'tihi' ? 'TiHi' : 'SOH'} column mapping — sheet{' '}
              <code className="font-mono">{masterPreview.sheet}</code>, header row {masterPreview.header_row},{' '}
              {masterPreview.row_count} row(s) parsed, {masterPreview.skipped} skipped
              {masterPreview.status === 'needs_mapping' && (
                <span className="ml-2 text-amber-700 font-semibold">— needs manual mapping</span>
              )}
            </p>
            <div className="flex flex-wrap gap-3">
              {Object.entries(MASTER_FIELD_LABELS[masterPreview.kind]).map(([fieldKey, label]) => {
                const m = masterPreview.mapping[fieldKey];
                return (
                  <label key={fieldKey} className="text-xs space-y-1">
                    <span className="block font-medium">
                      {label}
                      {m?.column && m.method !== 'manual' && (
                        <span className="ml-1 font-normal text-muted-foreground">
                          ({m.method} {Math.round(m.confidence * 100)}%)
                        </span>
                      )}
                    </span>
                    <select
                      value={masterMapping[fieldKey] ?? ''}
                      onChange={e => setMasterMapping(mm => ({ ...mm, [fieldKey]: e.target.value }))}
                      className="rounded border px-2 py-1 text-xs max-w-[230px]"
                    >
                      <option value="">— not in file —</option>
                      {masterPreview.columns.map(c => (
                        <option key={c.name} value={c.name} title={c.samples.join(', ')}>
                          {c.name}{c.samples.length ? ` (e.g. ${c.samples[0]})` : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                );
              })}
            </div>
            {masterPreview.warnings.map((w, i) => (
              <p key={i} className="text-xs text-amber-700">{w}</p>
            ))}
            <div className="flex gap-2">
              <button
                onClick={commitMaster}
                disabled={masterBusy}
                className="rounded-lg bg-gray-900 text-white px-3 py-1.5 text-xs font-medium hover:bg-gray-700 disabled:opacity-50"
              >
                {masterBusy ? 'Working…' : `Commit ${masterPreview.kind === 'tihi' ? 'TiHi' : 'SOH'}`}
              </button>
              <button
                onClick={() => { setMasterPreview(null); setMasterFile(null); }}
                className="rounded-lg border px-3 py-1.5 text-xs font-medium hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>

      {/* ── ② Parameters + ③ run ────────────────────────────────────────────── */}
      <section className="rounded-xl border bg-white p-4 space-y-3">
        <h3 className="font-semibold text-sm">Counting parameters</h3>
        <div className="flex flex-wrap items-end gap-4">
          <label className="text-xs space-y-1">
            <span className="block font-medium">
              Ti — cartons per layer {tihiAvailable
                ? <span className="font-normal text-muted-foreground">(blank = auto from TiHi)</span>
                : '*'}
            </span>
            <input
              type="number" min={1} value={tiStr}
              onChange={e => setTiStr(e.target.value)}
              className="w-28 rounded border px-2 py-1.5 text-sm"
              placeholder={tihiAvailable ? 'auto' : 'e.g. 10'}
            />
          </label>
          <label className="text-xs space-y-1">
            <span className="block font-medium">Hi — layers <span className="font-normal text-muted-foreground">(blank = auto from YOLO)</span></span>
            <input
              type="number" min={1} value={hiStr}
              onChange={e => setHiStr(e.target.value)}
              className="w-28 rounded border px-2 py-1.5 text-sm"
              placeholder="auto"
            />
          </label>
          <label className="text-xs space-y-1">
            <span className="block font-medium">Y-shift threshold floor (px)</span>
            <input
              type="number" min={1} step={1} value={thrStr}
              onChange={e => setThrStr(e.target.value)}
              className="w-28 rounded border px-2 py-1.5 text-sm"
            />
          </label>
          {status && status.models.length > 0 && (
            <label className="text-xs space-y-1">
              <span className="block font-medium">
                Model <span className="font-normal text-muted-foreground">(local /data/models)</span>
                {modelWarm === 'warming' && <span className="ml-1 text-amber-600">⏳ warming…</span>}
                {modelWarm === 'ready' && <span className="ml-1 text-green-600">✓ ready</span>}
              </span>
              <select
                value={selectedModel}
                onChange={e => setSelectedModel(e.target.value)}
                className="rounded border px-2 py-1.5 text-sm max-w-[280px]"
              >
                {status.models.map(m => (
                  <option key={m.filename} value={m.filename}>
                    {m.filename} ({m.size_mb} MB){m.is_default ? ' — production' : ''}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div className="flex items-center gap-2 ml-auto">
            {paramsDiffer && (
              <button
                onClick={recountActive}
                disabled={recounting}
                className="rounded-lg border border-indigo-300 bg-indigo-50 text-indigo-800 px-3 py-1.5 text-sm font-medium hover:bg-indigo-100 disabled:opacity-50"
                title="Re-runs only the counter on the boxes already detected — no image upload, no YOLO pass"
              >
                {recounting ? 'Recounting…' : `↺ Recount ${activeRun?.label}`}
              </button>
            )}
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={running || !paramsValid}
              className="rounded-lg bg-gray-900 text-white px-3 py-1.5 text-sm font-medium hover:bg-gray-700 disabled:opacity-50"
            >
              {running
                ? `Running… ${progress.done}/${progress.total}`
                : `▶ Upload photos & run (max ${MAX_TOTAL_FILES})`}
            </button>
          </div>
        </div>
        <input
          ref={fileInputRef} type="file" accept=".jpg,.jpeg,.png" multiple hidden
          onChange={e => e.target.files && runOnFiles(e.target.files)}
        />
        <div className="flex flex-wrap gap-x-5 gap-y-1.5 pt-1 border-t">
          <span className="text-xs font-medium text-muted-foreground w-full">
            Counting techniques <span className="font-normal">(benchmark here before production)</span>
          </span>
          {Object.entries(TECHNIQUE_LABELS).map(([key, label]) => (
            <label key={key} className="text-xs flex items-center gap-1.5 cursor-pointer">
              <input
                type="checkbox"
                checked={techniques[key] ?? false}
                onChange={e => setTechniques(t => ({ ...t, [key]: e.target.checked }))}
              />
              {label}
            </label>
          ))}
        </div>
        {!paramsValid && tiStr !== '' && (
          <p className="text-xs text-red-600">Ti must be ≥ 1; Hi blank or ≥ 1; threshold &gt; 0.</p>
        )}
        {runError && <p className="text-xs text-red-600">{runError}</p>}
      </section>

      {/* ── ⑥ Comparison table ──────────────────────────────────────────────── */}
      {runs.length > 0 && (
        <section className="rounded-xl border bg-white p-4 space-y-2">
          <h3 className="font-semibold text-sm">Runs this session</h3>
          <div className="overflow-x-auto">
            <table className="text-xs w-full">
              <thead>
                <tr className="text-left text-muted-foreground border-b">
                  <th className="py-1.5 pr-3">Run</th>
                  <th className="py-1.5 pr-3">Model</th>
                  <th className="py-1.5 pr-3">Ti</th>
                  <th className="py-1.5 pr-3">Hi</th>
                  <th className="py-1.5 pr-3">Thr px</th>
                  <th className="py-1.5 pr-3">Files</th>
                  <th className="py-1.5 pr-3">Counted</th>
                  <th className="py-1.5 pr-3">⚠ Audit</th>
                  <th className="py-1.5 pr-3">Avg inference</th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => {
                  const counted = r.files.filter(f => f.count != null);
                  const audited = counted.filter(f => f.count!.requires_human_audit).length;
                  const infTimes = r.files.filter(f => !f.error).map(f => f.inference_ms);
                  const avgInf = infTimes.length ? infTimes.reduce((a, b) => a + b, 0) / infTimes.length : 0;
                  return (
                    <tr
                      key={r.id}
                      onClick={() => setActiveRunId(r.id)}
                      className={`border-b last:border-0 cursor-pointer hover:bg-gray-50 ${r.id === activeRunId ? 'bg-indigo-50/60' : ''}`}
                    >
                      <td className="py-1.5 pr-3 font-medium">
                        {r.label}{r.recounted && <span className="ml-1 text-indigo-700" title="Counter re-run with new parameters — previews show the original run's row colours">↺</span>}
                      </td>
                      <td className="py-1.5 pr-3 font-mono truncate max-w-[160px]" title={r.model}>{r.model}</td>
                      <td className="py-1.5 pr-3 font-mono">{r.ti}</td>
                      <td className="py-1.5 pr-3 font-mono">{r.hi ?? 'auto'}</td>
                      <td className="py-1.5 pr-3 font-mono">{r.yThreshold}</td>
                      <td className="py-1.5 pr-3">{r.files.length}</td>
                      <td className="py-1.5 pr-3">{counted.length}</td>
                      <td className="py-1.5 pr-3">{audited > 0 ? <span className="text-amber-700 font-semibold">{audited}</span> : '0'}</td>
                      <td className="py-1.5 pr-3 font-mono">{avgInf.toFixed(0)} ms</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* ── ④ Per-file results ──────────────────────────────────────────────── */}
      {activeRun && (
        <section className="rounded-xl border bg-white p-4 space-y-3">
          <h3 className="font-semibold text-sm">
            {activeRun.label} — per-file results
            <span className="ml-2 text-xs font-normal text-muted-foreground font-mono">
              Ti={activeRun.ti} · Hi={activeRun.hi ?? 'auto'} · thr={activeRun.yThreshold}px
            </span>
            {activeRun.recounted && (
              <span className="ml-2 text-[10px] font-normal text-indigo-700">
                ↺ recounted — preview row colours reflect the original detection run
              </span>
            )}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {activeRun.files.map(f => (
              <div key={f.filename + f.total_ms} className="rounded-lg border overflow-hidden">
                <div
                  className="bg-gray-100 aspect-video flex items-center justify-center cursor-zoom-in overflow-hidden"
                  onClick={() => f.preview_b64 && setZoomFile(f)}
                  title="Click to enlarge"
                >
                  {f.preview_b64
                    ? <img src={f.preview_b64} alt={f.filename} className="w-full h-full object-cover" />
                    : <span className="text-xs text-muted-foreground">no preview</span>}
                </div>
                <div className="p-2.5 space-y-1.5">
                  <div className="flex items-center gap-1.5">
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${f.count && !f.count.requires_human_audit ? 'bg-green-100 text-green-800' : f.count ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-700'}`}>
                      {f.count ? (f.count.requires_human_audit ? '⚠' : '✓') : '✗'}
                    </span>
                    {f.reconciliation && (
                      <span
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${RECON_BADGE[f.reconciliation.state]?.cls ?? 'bg-gray-100 text-gray-600'}`}
                        title={f.reconciliation.detail}
                      >
                        {RECON_BADGE[f.reconciliation.state]?.label ?? f.reconciliation.state}
                      </span>
                    )}
                    {f.scene_class && (f.scene_class !== 'Unknown' || f.scene_source === 'legacy_model') && (
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 ${SCENE_BADGE[f.scene_class] ?? 'bg-gray-100 text-gray-600'}`}
                        title={`${f.scene_source === 'legacy_model' ? 'Seven-class model (operational)' : 'Heuristic (operational)'}`
                          + (f.scene_strategy ? ` · ${f.scene_strategy}` : '')}>
                        {f.scene_class}
                        {f.scene_source === 'legacy_model' && typeof f.scene_confidence === 'number'
                          ? ` ${Math.round(f.scene_confidence * 100)}%·M` : ''}
                      </span>
                    )}
                    {f.shadow_scene_class && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded shrink-0 bg-slate-100 text-slate-600"
                        title={`Shadow model prediction (non-authoritative)${f.shadow_blocked ? ' — blocked' : ''}`}>
                        shadow:{f.shadow_scene_class}
                        {typeof f.shadow_confidence === 'number' ? ` ${Math.round(f.shadow_confidence * 100)}%` : ''}
                        {f.scene_agreement === false ? ' ✗' : f.scene_agreement === true ? ' ✓' : ''}
                      </span>
                    )}
                    {f.simulation_mode && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">
                        simulation
                      </span>
                    )}
                    <span className="text-xs font-mono truncate" title={f.filename}>{f.filename}</span>
                    <span className="text-xs text-muted-foreground ml-auto shrink-0">{f.inference_ms.toFixed(0)} ms</span>
                  </div>
                  {f.reconciliation && f.reconciliation.state !== 'MATCH' && (
                    <p className="text-xs rounded bg-gray-50 border p-1.5 text-gray-700">
                      {f.reconciliation.detail}
                    </p>
                  )}

                  {f.count && (
                    <>
                      <p className="text-sm">
                        <span className="text-[10px] font-semibold text-gray-500 uppercase mr-1">Total</span>
                        <span className="text-lg font-bold">{f.count.total_cartons}</span>
                        <span className="ml-1.5 text-xs text-muted-foreground font-mono">
                          = ({(f.hi_used ?? 0)}−1)×{f.ti_used ?? activeRun.ti ?? '?'} + {f.count.top_layer_cartons}
                        </span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        front <span className="font-semibold text-green-700">{f.count.front_row_count}</span>
                        {' · '}back <span className="font-semibold text-blue-700">{f.count.back_row_count}</span>
                        {' · '}Ti {f.ti_used ?? '—'}
                        {f.ti_source && (
                          <span className={`ml-1 px-1 py-0.5 rounded text-[10px] font-semibold ${f.ti_source === 'tihi' ? 'bg-indigo-100 text-indigo-800' : 'bg-gray-100 text-gray-700'}`}>
                            {f.ti_source}
                          </span>
                        )}
                        {' · '}Hi {f.hi_used}
                        {f.hi_source && (
                          <span className={`ml-1 px-1 py-0.5 rounded text-[10px] font-semibold ${f.hi_source === 'detected' ? 'bg-sky-100 text-sky-800' : 'bg-gray-100 text-gray-700'}`}>
                            {f.hi_source}
                          </span>
                        )}
                        {' · '}conf {(f.count.calculation_confidence * 100).toFixed(0)}%
                        {' · '}thr {f.count.effective_threshold_px.toFixed(0)}px
                      </p>
                      {f.count.requires_human_audit && (
                        <p className="text-xs rounded bg-amber-50 border border-amber-200 text-amber-800 p-1.5"
                           title={f.count.audit_reason ?? undefined}>
                          ⚠ {f.count.audit_reason}
                        </p>
                      )}
                    </>
                  )}
                  {f.count_error && (
                    <p className="text-xs rounded bg-orange-50 border border-orange-200 text-orange-800 p-1.5">
                      {f.count_error}
                    </p>
                  )}
                  {f.error && <p className="text-xs text-red-600 truncate" title={f.error}>{f.error}</p>}

                  {(() => {
                    const excl = f.boxes.filter(b => b.row === 'excluded').length;
                    return (
                      <p className="text-[11px] text-muted-foreground">
                        YOLO: {f.boxes.length - excl} carton box{f.boxes.length - excl === 1 ? '' : 'es'}
                        {excl > 0 && <> · <span className="text-gray-600 font-semibold">{excl} excluded</span> (neighbour pallet)</>}
                        {(f.pallet_count ?? 0) > 0 && <> · {f.pallet_count} pallet{f.pallet_count === 1 ? '' : 's'}</>}
                        {f.detected_layers != null && <> · {f.detected_layers} layer{f.detected_layers === 1 ? '' : 's'}</>}
                        {f.yolo_confidence > 0 && <> · conf {(f.yolo_confidence * 100).toFixed(0)}%</>}
                      </p>
                    );
                  })()}
                  {f.techniques && f.techniques.some(t => t.enabled) && (
                    <details className="text-[11px] text-muted-foreground">
                      <summary className="cursor-pointer">Techniques</summary>
                      <ul className="mt-0.5 space-y-0.5">
                        {f.techniques.filter(t => t.enabled).map(t => (
                          <li key={t.name}>
                            <span className={t.applied ? 'text-gray-800' : ''}>
                              {t.applied ? '●' : '○'} {t.name}: {t.summary}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {zoomFile && <PreviewModal file={zoomFile} onClose={() => setZoomFile(null)} />}
    </div>
  );
}
